import React, { useState, useRef, useEffect } from "react";

// Environment dropdown, styled like the console's ConsoleWebsiteSelector.
// Each environment maps to one S3 bucket (zenyt-<env>-agentic). Prod is
// flagged so the user always knows when they're about to touch production.

function EnvironmentSelector({ environments, value, onChange, isLoading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = environments.find((e) => e.env === value) || null;

  if (isLoading) {
    return <div className="h-9 w-56 bg-surface-neutral-subtle rounded-lg animate-pulse" />;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-border-default rounded-xl hover:bg-surface-neutral transition-colors min-w-[200px]"
      >
        <span className="text-body-xs text-text-tertiary font-medium uppercase tracking-wide">
          Env
        </span>
        <span className="text-[14px] font-semibold text-text-primary truncate">
          {selected ? selected.env : "—"}
        </span>
        {selected?.is_prod && (
          <span className="text-caption font-bold uppercase px-1.5 py-0.5 rounded-md bg-severity-high/10 text-severity-high">
            prod
          </span>
        )}
        <svg
          className={`w-4 h-4 text-text-tertiary ml-auto transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-72 bg-white rounded-2xl shadow-lg border border-border-subtle z-50 overflow-hidden animate-fade-in">
          <div className="max-h-72 overflow-auto p-1 scrollbar-thin">
            {environments.map((e) => {
              const isSel = e.env === value;
              return (
                <button
                  key={e.env}
                  onClick={() => {
                    onChange(e.env);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    isSel
                      ? "bg-surface-neutral"
                      : "hover:bg-surface-neutral"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-body-sm font-medium text-text-primary">
                      {e.env}
                    </span>
                    {e.is_prod && (
                      <span className="text-caption font-bold uppercase px-1.5 py-0.5 rounded-md bg-severity-high/10 text-severity-high">
                        prod
                      </span>
                    )}
                  </div>
                  <div className="text-caption text-text-tertiary font-mono mt-0.5">
                    {e.bucket}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default EnvironmentSelector;
